import i18n from 'config/i18n';
import { isNil } from 'ramda';
import * as yup from 'yup';

import { getLocale } from 'utils/date';

const t = i18n.getFixedT(getLocale());

const travelSetLinesSchema = {
  travelSetLines: yup.lazy((arr) => {
    if (isNil(arr)) {
      return yup.array();
    }
    return yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number(),
          trvSetLineId: yup.number(),
          trvSetId: yup.number(),
          distanceRangeFrom: yup
            .number()
            .min(0, t('validation.greaterThan', { field: 0 }))
            .max(9999, t('validation.lessThan', { field: 9999 }))
            .required(
              t('validationFieldRequired', {
                name: t('adminPortal.arrangements.details.from')
              })
            )
            .typeError(
              t('validation.invalid', {
                name: t('adminPortal.arrangements.details.from')
              })
            ),
          distanceRangeTo: yup
            .number()
            .min(0, t('validation.greaterThan', { field: 0 }))
            .max(9999, t('validation.lessThan', { field: 9999 }))
            .required(
              t('validationFieldRequired', {
                name: t('adminPortal.arrangements.details.to')
              })
            )
            .typeError(
              t('validation.invalid', {
                name: t('adminPortal.arrangements.details.to')
              })
            )
            .moreThan(
              yup.ref('distanceRangeFrom'),
              t('validation.greaterThan', {
                field: t('adminPortal.arrangements.details.from')
              })
            ),
          expensesPerDistance: yup
            .number()
            .nullable()
            .transform((value) => (!value ? null : value))
            .typeError(
              t('validation.invalid', {
                name: t('adminPortal.arrangements.details.perKm')
              })
            )
            .min(0, t('validation.greaterThan', { field: 0 })),
          expensesNominal: yup
            .number()
            .nullable()
            .transform((value) => (!value ? null : value))
            .typeError(
              t('validation.invalid', {
                name: t('adminPortal.arrangements.details.nominal')
              })
            )
            .min(0, t('validation.greaterThan', { field: 0 })),
          expensesMax: yup
            .number()
            .nullable()
            .transform((value) => (!value ? null : value))
            .typeError(
              t('validation.invalid', {
                name: t('adminPortal.arrangements.details.max')
              })
            )
            .min(0, t('validation.greaterThan', { field: 0 })),
          valid: yup.object({
            from: yup
              .date()
              .required(t('validationFieldRequired', { name: t('validFrom') }))
              .nullable()
              .default(null)
              .typeError(t('adminPortal.arrangements.invalidDate')),
            to: yup
              .date()
              .required(t('validationFieldRequired', { name: t('validTo') }))
              .nullable()
              .default(null)
              .typeError(t('adminPortal.arrangements.invalidDate'))
              .min(
                yup.ref('from'),
                t('adminPortal.arrangements.minEndDate', { from: t('from') })
              )
          })
        })
      )
      .required();
  })
};

const travelSetLineSchema = {
  travelSetLine: yup.object({
    distanceRangeFrom: yup
      .number()
      .required(
        t('validationFieldRequired', {
          name: t('adminPortal.arrangements.details.from')
        })
      )
      .typeError(
        t('validationFieldRequired', {
          name: t('adminPortal.arrangements.details.from')
        })
      )
      .min(0, t('validation.greaterThan', { field: 0 }))
      .max(9999, t('validation.lessThan', { field: 9999 })),
    distanceRangeTo: yup
      .number()
      .required(
        t('validationFieldRequired', {
          name: t('adminPortal.arrangements.details.to')
        })
      )
      .min(0, t('validation.greaterThan', { field: 0 }))
      .max(9999, t('validation.lessThan', { field: 9999 }))
      .typeError(
        t('validation.invalid', {
          name: t('adminPortal.arrangements.details.to')
        })
      )
      .moreThan(
        yup.ref('distanceRangeFrom'),
        t('validation.greaterThan', {
          field: t('adminPortal.arrangements.details.from')
        })
      ),
    expensesPerDistance: yup
      .number()
      .nullable()
      .transform((value) => (!value ? null : value))
      .typeError(
        t('validation.invalid', {
          name: t('adminPortal.arrangements.details.perKm')
        })
      )
      .min(0, t('validation.greaterThan', { field: 0 })),
    expensesNominal: yup
      .number()
      .nullable()
      .transform((value) => (!value ? null : value))
      .typeError(
        t('validation.invalid', {
          name: t('adminPortal.arrangements.details.nominal')
        })
      )
      .min(0, t('validation.greaterThan', { field: 0 })),
    expensesMax: yup
      .number()
      .nullable()
      .typeError(
        t('validation.invalid', {
          name: t('adminPortal.arrangements.details.max')
        })
      )
      .min(0, t('validation.greaterThan', { field: 0 })),
    from: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validFrom') }))
      .typeError(t('adminPortal.arrangements.invalidDate'))
      .nullable()
      .default(null),
    to: yup
      .date()
      .required(t('validationFieldRequired', { name: t('validTo') }))
      .typeError(t('adminPortal.arrangements.invalidDate'))
      .nullable()
      .default(null)
      .min(
        yup.ref('startDate'),
        t('adminPortal.arrangements.minEndDate', { start: t('startDate') })
      )
  })
};

const arrangementSchema = yup.object({
  id: yup.number(),
  code: yup
    .string()
    .required(
      t('validationFieldRequired', {
        name: t('adminPortal.arrangements.code')
      })
    )
    .default(''),
  codeExtern: yup.string().default(''),
  description: yup
    .string()
    .required(
      t('validationFieldRequired', {
        name: t('adminPortal.arrangements.description')
      })
    )
    .default(''),
  compensationTypeId: yup
    .number()
    .required(
      t('validationFieldRequired', {
        name: t('adminPortal.arrangements.typeOfArrangement')
      })
    )
    .typeError(
      t('validation.invalid', {
        name: t('adminPortal.arrangements.typeOfArrangement')
      })
    ),
  isExternal: yup.boolean().default(false)
});

export const createArrangementSchema = arrangementSchema.concat(
  yup.object().shape(travelSetLineSchema)
);

export const editArrangementSchema = arrangementSchema.concat(
  yup.object().shape(travelSetLinesSchema)
);
